import { useState, useEffect } from 'react';
import { RepasItem } from './components/AlimentSelector';
import AlimentSelector from './components/AlimentSelector';
import RepasList from './components/RepasList';

export default function PlatPersonnalisePage() {
  const [repas, setRepas] = useState<RepasItem[]>([]);
  const [showRepas, setShowRepas] = useState(false);

  // Auto-open repas panel on mobile when first item added
  useEffect(() => {
    if (repas.length === 1) {
      setShowRepas(true);
    }
  }, [repas.length]);

  const handleQuantiteChange = (id: number, quantite: number) => {
    setRepas((prev) => prev.map((r) => r.aliment.id === id ? { ...r, quantite } : r));
  };

  const handleRemove = (id: number) => {
    setRepas((prev) => prev.filter((r) => r.aliment.id !== id));
  };

  const totalGlucides = Math.round(
    repas.reduce((sum, r) => sum + (r.aliment.glucidesPour100g * r.quantite) / 100, 0)
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <div className="text-center">
            <span className="inline-block bg-teal-50 text-teal-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-3">
              Personnalisé
            </span>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Plat Personnalisé</h1>
            <p className="text-gray-500 text-base max-w-xl mx-auto">
              Sélectionnez vos aliments, ajustez les grammages et obtenez le total de glucides en temps réel.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* Left: Aliment selector */}
          <div className="flex-1 bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Bibliothèque d'aliments</h2>
                <p className="text-gray-500 text-xs mt-0.5">Cliquez sur <strong>+</strong> pour ajouter un aliment à votre repas</p>
              </div>
              {/* Mobile: toggle repas panel */}
              <button
                onClick={() => setShowRepas(true)}
                className="lg:hidden relative flex items-center gap-2 bg-teal-500 text-white text-sm font-semibold px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap active:scale-95 transition-transform"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/>
                  <path d="M7 2v20"/>
                  <path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>
                </svg>
                Mon repas
                {repas.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full">
                    {repas.length}
                  </span>
                )}
              </button>
            </div>
            <AlimentSelector repas={repas} onRepasChange={setRepas} />
          </div>

          {/* Right: Repas list — desktop always visible */}
          <div className="hidden lg:block lg:w-96">
            <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6 sticky top-24">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Mon repas</h2>
                  {repas.length > 0 && (
                    <p className="text-teal-600 text-xs font-semibold mt-0.5">{totalGlucides}g de glucides</p>
                  )}
                </div>
                {repas.length > 0 && (
                  <span className="w-6 h-6 flex items-center justify-center bg-teal-500 text-white text-xs font-bold rounded-full">
                    {repas.length}
                  </span>
                )}
              </div>
              <RepasList
                repas={repas}
                onQuantiteChange={handleQuantiteChange}
                onRemove={handleRemove}
              />
            </div>
          </div>
        </div>

        {/* Mobile Drawer Overlay */}
        {showRepas && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setShowRepas(false)}
            />
            {/* Drawer */}
            <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white z-50 shadow-2xl lg:hidden flex flex-col">
              {/* Drawer Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-white">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Mon repas</h2>
                  {repas.length > 0 && (
                    <p className="text-teal-600 text-xs font-semibold mt-0.5">{totalGlucides}g de glucides</p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {repas.length > 0 && (
                    <span className="w-6 h-6 flex items-center justify-center bg-teal-500 text-white text-xs font-bold rounded-full">
                      {repas.length}
                    </span>
                  )}
                  <button
                    onClick={() => setShowRepas(false)}
                    className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full cursor-pointer transition-colors"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6 6 18"/>
                      <path d="m6 6 12 12"/>
                    </svg>
                  </button>
                </div>
              </div>
              {/* Drawer Content */}
              <div className="flex-1 overflow-y-auto p-4">
                <RepasList
                  repas={repas}
                  onQuantiteChange={handleQuantiteChange}
                  onRemove={handleRemove}
                />
              </div>
            </div>
          </>
        )}

        {/* Info card */}
        <div className="mt-8 bg-teal-50 border border-teal-200 rounded-2xl p-5 flex gap-4">
          <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-xl shrink-0">
            <svg className="w-5 h-5 text-teal-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
          </div>
          <div>
            <h4 className="font-semibold text-teal-800 mb-1">Comment utiliser le Plat Personnalisé ?</h4>
            <p className="text-teal-700 text-sm leading-relaxed">
              Parcourez la bibliothèque d'aliments, cliquez sur <strong>+</strong> pour les ajouter à votre repas. 
              Ajustez ensuite les grammages dans la liste à droite — les glucides se recalculent automatiquement. 
              Le total vous aide à anticiper l'impact glycémique de votre repas.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
